import { getAppOrigin } from 'constants/site';
import { safeRedirectPath } from 'features/auth/lib/safeRedirectPath';
import { getSupabase, isSupabaseConfigured } from 'shared/supabase/client';
import { detectTimezone } from 'shared/lib/timezone';
import type { User } from 'types';

export interface AuthResult {
  user: User;
  token: string;
}

const LOGIN_NOT_ALLOWED_MSG =
  'Early access only. This email is not authorized to sign in yet.';

interface LoginAllowlistRpcResult {
  ok?: boolean;
  message?: string;
}

async function assertEmailLoginAllowed(email: string): Promise<void> {
  if (!isSupabaseConfigured) return;

  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('core_check_email_login_allowed', {
    p_email: email.trim(),
  });

  if (error) {
    throw new Error('Unable to verify login eligibility. Please try again later.');
  }

  const result = data as LoginAllowlistRpcResult | null;
  if (result?.ok === false) {
    throw new Error(result.message ?? LOGIN_NOT_ALLOWED_MSG);
  }
}

async function assertSessionLoginAllowed(): Promise<void> {
  if (!isSupabaseConfigured) return;

  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('core_assert_session_login_allowed');

  if (error) {
    throw new Error('Unable to verify session eligibility. Please try again later.');
  }

  const result = data as LoginAllowlistRpcResult | null;
  if (result?.ok === false) {
    await signOut();
    throw new Error(result.message ?? LOGIN_NOT_ALLOWED_MSG);
  }
}

/**
 * First login after migration 144 has no stored zone. Persist the detected one
 * so server-side senders (emails) agree with the UI, and flag it so the app can
 * disclose the choice once instead of silently deciding for the sadhak.
 */
async function ensureUserTimezone(
  stored: string | null
): Promise<{ timezone: string | null; justDetected: boolean }> {
  if (stored) return { timezone: stored, justDetected: false };

  const detected = detectTimezone();
  if (!detected) return { timezone: null, justDetected: false };

  try {
    const saved = await saveUserTimezone(detected);
    return { timezone: saved, justDetected: true };
  } catch (err) {
    // Never block sign-in on a preference write; rendering falls back to detection.
    console.warn('Could not save detected timezone:', err);
    return { timezone: null, justDetected: false };
  }
}

/** Validated, column-scoped write (migration 144). Returns the stored zone. */
export async function saveUserTimezone(timezone: string): Promise<string> {
  if (!isSupabaseConfigured) return timezone;

  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('core_set_user_timezone', {
    p_timezone: timezone,
  });

  if (error) throw new Error(error.message);
  return typeof data === 'string' && data ? data : timezone;
}

async function mapSupabaseUser(authUserId: string, accessToken: string): Promise<AuthResult> {
  const supabase = getSupabase();

  const { data: profile, error } = await supabase
    .from('core_users')
    .select('id, email, first_name, last_name, avatar_url, timezone')
    .eq('auth_user_id', authUserId)
    .is('deleted_at', null)
    .single();

  if (error || !profile) {
    throw new Error('Takshify profile not found. Please contact support.');
  }

  const { timezone, justDetected: timezoneJustDetected } = await ensureUserTimezone(
    (profile as { timezone?: string | null }).timezone ?? null
  );

  const { data: roleRows, error: rolesError } = await supabase
    .from('core_user_roles')
    .select('role_id, core_roles!inner(slug)')
    .eq('user_id', profile.id)
    .is('deleted_at', null);

  if (rolesError) {
    console.warn('Failed to load roles:', rolesError.message);
  }

  const roles =
    roleRows
      ?.map((r) => {
        const role = r.core_roles as { slug: string } | { slug: string }[] | null;
        if (Array.isArray(role)) return role[0]?.slug;
        return role?.slug;
      })
      .filter((s): s is string => Boolean(s)) ?? ['student'];

  const { data: enrollmentRows, error: enrollmentError } = await supabase
    .from('inst_batch_enrollments')
    .select(`
      id,
      inst_institutions ( name ),
      inst_batches ( inst_institutions ( name ) )
    `)
    .eq('user_id', profile.id)
    .eq('status', 'active')
    .is('deleted_at', null);

  if (enrollmentError) {
    throw new Error(enrollmentError.message);
  }

  const institutionNames = Array.from(
    new Set(
      (enrollmentRows ?? [])
        .map((row) => {
          const direct = row.inst_institutions as { name?: string } | { name?: string }[] | null;
          if (Array.isArray(direct) && direct[0]?.name) return direct[0].name;
          if (direct && !Array.isArray(direct) && direct.name) return direct.name;

          const batch = row.inst_batches as
            | { inst_institutions?: { name?: string } | { name?: string }[] | null }
            | { inst_institutions?: { name?: string } | { name?: string }[] | null }[]
            | null;
          const batchRow = Array.isArray(batch) ? batch[0] : batch;
          const viaBatch = batchRow?.inst_institutions;
          if (Array.isArray(viaBatch) && viaBatch[0]?.name) return viaBatch[0].name;
          if (viaBatch && !Array.isArray(viaBatch) && viaBatch.name) return viaBatch.name;
          return null;
        })
        .filter((name): name is string => Boolean(name))
    )
  );

  const { data: latestJoinRequest, error: joinRequestError } = await supabase
    .from('inst_join_requests')
    .select('status')
    .eq('requester_email', profile.email.toLowerCase())
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (joinRequestError) {
    throw new Error(joinRequestError.message);
  }

  const hasApprovedMembership = (enrollmentRows?.length ?? 0) > 0;
  const latestJoinRequestStatus = (latestJoinRequest?.status as 'pending' | 'approved' | 'declined' | undefined) ?? null;

  let hasSoftHomeAffiliation = false;
  let softHomeInstitutionId: string | null = null;
  let softHomeInstitutionName: string | null = null;
  let softHomeWelcomePending = false;

  // Soft-home for orphans when platform default institute is enabled (migration 141).
  try {
    const { data: softHomeData, error: softHomeError } = await supabase.rpc(
      'inst_ensure_soft_home_affiliation'
    );
    if (!softHomeError && softHomeData && typeof softHomeData === 'object') {
      const soft = softHomeData as {
        ok?: boolean;
        affiliated?: boolean;
        institution_id?: string;
        institution_name?: string;
        show_welcome?: boolean;
      };
      if (soft.ok && soft.affiliated) {
        hasSoftHomeAffiliation = true;
        softHomeInstitutionId = soft.institution_id ?? null;
        softHomeInstitutionName = soft.institution_name ?? null;
        softHomeWelcomePending = Boolean(soft.show_welcome);
        if (softHomeInstitutionName && !institutionNames.includes(softHomeInstitutionName)) {
          institutionNames.push(softHomeInstitutionName);
        }
      }
    }
  } catch (err) {
    console.warn('Soft-home affiliation check skipped:', err);
  }

  const user: User = {
    id: profile.id,
    email: profile.email,
    firstName: profile.first_name,
    lastName: profile.last_name,
    avatarUrl: profile.avatar_url ?? undefined,
    roles,
    hasApprovedMembership,
    hasSoftHomeAffiliation,
    softHomeInstitutionId,
    softHomeInstitutionName,
    softHomeWelcomePending,
    institutionNames,
    pendingJoinRequest: latestJoinRequestStatus === 'pending',
    latestJoinRequestStatus,
    timezone,
    timezoneJustDetected,
    createdAt: new Date().toISOString(),
  };

  return { user, token: accessToken };
}

/** Google OAuth - redirect flow */
export async function signInWithGoogle(redirectPath = '/dashboard'): Promise<void> {
  const supabase = getSupabase();
  const path = safeRedirectPath(redirectPath, '/dashboard');
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${getAppOrigin()}${path}`,
    },
  });
  if (error) throw new Error(error.message);
}

/** Email magic link - no password */
export async function sendMagicLink(email: string, redirectPath = '/dashboard'): Promise<void> {
  await assertEmailLoginAllowed(email);

  const supabase = getSupabase();
  const path = safeRedirectPath(redirectPath, '/dashboard');
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}${path}`,
      shouldCreateUser: true,
    },
  });
  if (error) throw new Error(error.message);
}

/** Demo mode only - password login via MSW */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<AuthResult> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
  if (!data.session) throw new Error('No session returned');
  return mapSupabaseUser(data.user.id, data.session.access_token);
}

export async function signOut(): Promise<void> {
  if (!isSupabaseConfigured) return;

  const supabase = getSupabase();
  // Local scope avoids a network round-trip that can hang (global revoke).
  // Timeout + swallow errors so the UI never waits on Auth.
  try {
    await Promise.race([
      supabase.auth.signOut({ scope: 'local' }),
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, 2500);
      }),
    ]);
  } catch (err) {
    console.warn('Sign out did not complete cleanly:', err);
  }
}

export async function getSessionAuth(): Promise<AuthResult | null> {
  const supabase = getSupabase();
  const { data } = await supabase.auth.getSession();
  if (!data.session) return null;

  await assertSessionLoginAllowed();

  return mapSupabaseUser(data.session.user.id, data.session.access_token);
}

export async function markSoftHomeWelcomeSeen(): Promise<void> {
  if (!isSupabaseConfigured) return;
  const supabase = getSupabase();
  const { error } = await supabase.rpc('inst_mark_soft_home_welcome_seen');
  if (error) throw new Error(error.message);
}

export async function getAccessToken(): Promise<string | null> {
  if (!isSupabaseConfigured) {
    return localStorage.getItem('platform_auth_token');
  }
  const supabase = getSupabase();
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export { isSupabaseConfigured };
